import { useState, type FormEvent } from 'react';
import { Paperclip, FileCheck2, X } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/authStore';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';

export function RevocarSancionModal({
  open, proveedorID, sancionID, onClose, onDone,
}: {
  open: boolean;
  proveedorID: string;
  sancionID: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const token = useAuth((s) => s.token);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      let documento_id: string | undefined;
      if (archivo) {
        const fd = new FormData();
        fd.append('file', archivo);
        fd.append('modulo_origen', 'proveedores');
        fd.append('entidad', 'proveedor_sancion_revocacion');
        fd.append('entidad_id', sancionID);
        fd.append('tipo', 'oficio_revocacion_sancion');
        const res = await fetch('/api/documentos', {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        const body = await res.json();
        if (!res.ok) {
          if (res.status === 422 && body.clamav_estado === 'infectado') {
            throw new Error('El archivo fue detectado como malicioso por ClamAV.');
          }
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        documento_id = body.id;
      }
      await api(`/proveedores/${proveedorID}/sanciones/${sancionID}/revocar`, {
        method: 'POST',
        body: { motivo, documento_id },
      });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Revocar sanción"
      subtitle="La sanción quedará sin efecto desde ahora. La fila se conserva por auditoría con el motivo y usuario de la revocación."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">Cancelar</Button>
          <Button type="submit" form="revocar-form" loading={submitting}>Revocar sanción</Button>
        </>
      }
    >
      <form id="revocar-form" onSubmit={submit} className="flex flex-col gap-4">
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700 dark:bg-rose-900/40 dark:text-rose-200">{err}</div>}
        <div>
          <label className="text-[13px] font-medium text-ink">Motivo de revocación</label>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            required rows={3}
            placeholder="Ej: Pago completo de multa, revocación por autoridad superior, cumplimiento del contrato original, etc."
            className="mt-1.5 w-full rounded-md border border-border bg-surface px-3 py-2 text-[14px] shadow-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>
        <div>
          <label className="text-[13px] font-medium text-ink">
            Oficio de revocación <span className="text-ink-soft">(opcional)</span>
          </label>
          <p className="mt-0.5 text-[12px] text-ink-mute">PDF o imagen con la notificación oficial de la revocación.</p>
          {archivo ? (
            <div className="mt-2 flex items-center gap-3 rounded-md border border-border bg-canvas px-3 py-2.5">
              <FileCheck2 size={18} className="text-emerald-600" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium text-ink">{archivo.name}</div>
                <div className="text-[11px] text-ink-mute">{(archivo.size / 1024).toFixed(1)} KB</div>
              </div>
              <button type="button" onClick={() => setArchivo(null)} className="rounded-md p-1 text-ink-mute hover:bg-muted hover:text-danger">
                <X size={16} />
              </button>
            </div>
          ) : (
            <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border bg-canvas py-4 text-[13px] text-ink-mute transition-colors hover:border-brand-500 hover:text-ink">
              <Paperclip size={15} />
              Seleccionar archivo
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
              />
            </label>
          )}
        </div>
      </form>
    </Modal>
  );
}
